import React, { useState, useEffect } from "react";
import { FormGroup, Tag } from "reactstrap";
import {
    Formalizr,
    type FormalizrComponentMap,
    type FormalizrSchema,
} from "@2po/formalizr";
import { ContentfulFormalizrProvider } from "@utils/context/ContentfulFormalizerContext";
import { graphql, navigate } from "gatsby";
import { FormalizrForm, FormMapperApp } from "@types";
import { useContextStore } from "@hooks";
import {
    FormalizerHiddenField,
    FormalizerTextField,
    FormalizerEmailField,
    FormalizerCheckboxField,
    FormalizerTextAreaField,
    FormalizerVerticalLayout,
    FormalizerHorizontalLayout,
    FormalizerDropdownField,
    FormalizerButton,
    FormalizerDynamics,
    FormalizerButtonWrapper,
    FormalizerCheckboxGroup,
    FormalizerEntryField,
} from "./components";
import { submitDynamicsForm } from "@utils/dynamics";

type Props = {
    data: {
        node_locale: string;
        id: string;
        __typename: string;
        form: {
            __typename: string;
            internal: {
                content: string;
            };
        };
        formReferences: any;
        dynamicsFormId: string;
        formMapper: {
            __typename: string;
            internal: {
                content: string;
            };
        };
    };
};

const FormalizerForm = ({
    data: {
        form: formData,
        dynamicsFormId,
        formMapper: formMapperData,
        formReferences,
    },
}: Props) => {
    const { locale } = useContextStore();
    const [submitRedirect, setSubmitRedirect] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const form = JSON.parse(formData.internal.content) as FormalizrSchema;
    const formMapperAppData = {
        dynamicsId: dynamicsFormId,
        formMapper: JSON.parse(
            formMapperData.internal.content,
        ) as FormMapperApp,
    };

    useEffect(() => {
        return () => {
            setIsSubmitting(false);
        };
    }, []);

    const translations = {
        submitError: "Error while submitting form",
    };

    const calculateButtonProperties = (
        button: typeof form.output.ui.options.submitButton,
    ) => {
        if (!button?.display) {
            return;
        }

        return formReferences?.find(
            c => c?.contentful_id === button?.reference?.id,
        );
    };

    const submitButtonProps = calculateButtonProperties(
        form.output.ui.options.submitButton,
    );
    const resetButton = calculateButtonProperties(
        form.output.ui.options.resetButton,
    );

    const formalizrComponentMap: FormalizrComponentMap = {
        IntegerControl: FormalizerTextField,
        DropdownControl: FormalizerDropdownField,
        TextControl: FormalizerTextField,
        HiddenControl: FormalizerHiddenField,
        CheckboxControl: FormalizerCheckboxField,
        CheckboxGroupControl: FormalizerCheckboxGroup,
        TextMultilineControl: FormalizerTextAreaField,
        EmailControl: FormalizerEmailField,
        ButtonWrapper: FormalizerButtonWrapper,
        ContentfulEntry: props => (
            <FormalizerEntryField references={formReferences} {...props} />
        ),
        CategorizationStepper: () => <></>,
        CategorizationTabs: Tag,
        Category: () => <></>,
        HorizontalLayout: FormalizerHorizontalLayout,
        VerticalLayout: FormalizerVerticalLayout,
        Group: FormGroup,
        SubmitButton: () => (
            <FormalizerButton
                data={submitButtonProps}
                setSubmitRedirect={setSubmitRedirect}
            />
        ),
        ResetButton: () => <FormalizerButton data={resetButton} />,
        PreviousButton: FormalizerButton,
        NextButton: FormalizerButton,
    };

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            await submitDynamicsForm(
                form.internalData.name,
                locale,
                formMapperAppData,
            );
            if (submitRedirect) {
                navigate(submitRedirect);
            }
        } catch (error) {
            setIsSubmitting(false);
            console.error(error);
        }
    };

    const handleReset = (values: object) => {
        console.info("🚀 ~ handleReset ~ values:", values);
    };

    return (
        <div data-testid="form">
            <FormalizerDynamics
                form={form as FormalizrForm}
                locale={locale}
                mapperData={formMapperAppData.formMapper}
            />
            <ContentfulFormalizrProvider
                blacklistDomains={form.internalData.blacklistDomains}
                formName={form.internalData.name}
                isSubmitting={isSubmitting}
                locale={locale}
                translations={translations}
            >
                <Formalizr
                    componentMap={formalizrComponentMap}
                    initialValues={{}}
                    locale={locale}
                    onReset={handleReset}
                    onSubmit={handleSubmit}
                    schema={form}
                />
            </ContentfulFormalizrProvider>
        </div>
    );
};

export default FormalizerForm;

export const query = graphql`
    fragment FormalizerForm on ContentfulContentFormalizerForm {
        node_locale
        __typename
        id
        formReferences {
            ... on ContentfulContentCta {
                id
                contentful_id
                __typename
                targetBlank
                ctaText: text
                icon {
                    icon
                }
                externalLink
                internalLink {
                    ...NavLink
                }
                document {
                    slug
                    title
                    node_locale
                }
            }
            ...RichText
        }
        form {
            __typename
            internal {
                content
            }
        }
        dynamicsFormId
        formMapper {
            __typename
            internal {
                content
            }
        }
    }
`;
